import { useRef, useState } from 'react'
import type React from 'react'

/** Options for useResizableWidth. */
export interface ResizableWidthOptions {
  /** localStorage key the width is remembered under. */
  storageKey: string
  min: number
  max: number
  /** The widest the LAYOUT allows right now, in px — e.g. what is left once a
   *  neighbouring pane's floor is reserved. Read on every drag step, so it
   *  tracks a window resize. Omit for `max` alone. */
  room?: () => number
}

/** What the caller wires up: the remembered width (`null` = the caller's
 *  default), a ref for the resized element, and the props for its edge handle. */
export interface ResizableWidth {
  width: number | null
  ref: React.RefObject<HTMLDivElement>
  handleProps: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void
    onDoubleClick: () => void
  }
}

/** Data attribute marking the text a double-click fits the pane to. */
export const FIT_NAME_ATTR = 'data-fit-name'

function readStored(key: string, min: number, max: number): number | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= min && n <= max ? n : null
  } catch {
    // Private mode, or storage disabled. A remembered width is a convenience;
    // losing it must never stop the page rendering.
    return null
  }
}

/**
 * A left-hand pane whose RIGHT edge drags to resize it — the Fleet Editor's
 * Agents rail and the Workflows list, which both truncated the descriptive
 * names fleets give their agents and workflows.
 *
 * The contract matches the workflow inspector's edge: pointer capture so a fast
 * drag does not detach, arrow keys for the keyboard, bounded so no neighbour is
 * starved, remembered across reloads. Double-click fits the pane to its longest
 * name: each element marked `data-fit-name` is measured on ONE line, so this
 * works whether the name truncates or wraps.
 */
export function useResizableWidth({ storageKey, min, max, room }: ResizableWidthOptions): ResizableWidth {
  const [width, setWidth] = useState<number | null>(() =>
    typeof window === 'undefined' ? null : readStored(storageKey, min, max),
  )
  const ref = useRef<HTMLDivElement>(null)

  function clamp(px: number): number {
    const ceiling = Math.max(min, Math.min(max, room ? room() : max))
    return Math.min(Math.max(px, min), ceiling)
  }

  function resize(px: number) {
    const next = Math.round(clamp(px))
    setWidth(next)
    try {
      window.localStorage.setItem(storageKey, String(next))
    } catch {
      // Storage unavailable — the drag still works for this session.
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLElement>) {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const pane = ref.current
    if (!pane) return
    const onMove = (ev: PointerEvent) => resize(ev.clientX - pane.getBoundingClientRect().left)
    const onUp = (ev: PointerEvent) => {
      handle.releasePointerCapture(ev.pointerId)
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const current = ref.current?.getBoundingClientRect().width ?? width ?? min
    resize(current + (e.key === 'ArrowRight' ? 24 : -24))
  }

  /** Measured, not estimated. Each name is put on one line for the read and put
   *  back before the browser paints, so the overflow read is exactly the text
   *  that a truncation hides or a wrap pushes down. */
  function onDoubleClick() {
    const pane = ref.current
    if (!pane) return
    let hidden = 0
    pane.querySelectorAll<HTMLElement>(`[${FIT_NAME_ATTR}]`).forEach(el => {
      const prev = { ws: el.style.whiteSpace, clamp: el.style.webkitLineClamp, display: el.style.display }
      el.style.whiteSpace = 'nowrap'
      el.style.webkitLineClamp = 'unset'
      el.style.display = 'block'
      hidden = Math.max(hidden, el.scrollWidth - el.clientWidth)
      el.style.whiteSpace = prev.ws
      el.style.webkitLineClamp = prev.clamp
      el.style.display = prev.display
    })
    // +4 so the last glyph is not flush against the threshold. Nothing hidden
    // leaves the width alone rather than shrinking a pane the operator widened.
    if (hidden > 0) resize(pane.getBoundingClientRect().width + hidden + 4)
  }

  return { width, ref, handleProps: { onPointerDown, onKeyDown, onDoubleClick } }
}
